import { Query } from 'react-apollo';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import getMedia from '../../graphql/queries/media.graphql';
import createErrorMediaData from './utils/createErrorMediaData';
import personalAdConsent from '../../util/personalAdConsent';
import atInternetId from '../../util/atInternetId';
import getSitenameFromUrl from '../../util/sitenameFromUrl';
import {
  BNNVARA_VIDEO_PLAYER_TYPE,
  BNNVARA_AUDIO_PLAYER_TYPE,
  NPO_VIDEO_PLAYER_TYPE,
} from '../mediaplayers/MediaPlayer';

/**
 * This is a temporary transform function until we receive final data from the backend.
 * Missing data will be populated with mock data.
 *
 * @param data
 * @returns object
 */
const createMediaData = ({ mediaId, media }) => {
  let mediaData = {
    mediaId,
    type: media.type,
    title: media.title,
  };

  document.title = media.title;

  if (mediaData.type === NPO_VIDEO_PLAYER_TYPE) {
    mediaData = {
      ...mediaData,
      embedId: media.token,
    };
  }

  if (mediaData.type === BNNVARA_VIDEO_PLAYER_TYPE) {
    mediaData = {
      ...mediaData,
      prid: null,
      duration: media.duration,
      programTitle: media.title,
      genres: media.genres,
      site: getSitenameFromUrl(window.referrer || document.location.href),
      playername: 'bnnvara-web',
      classifications: media.classifications,
      broadcasters: media.broadcasters
        .map(broadcaster => broadcaster.name.toLowerCase())
        .join(','),
      poster: get(media, 'image.url', null),
      source: media.sources.map(source => ({
        src: source.url,
        type: source.type || 'video/mp4',
        name: source.name || 'HD',
        ratio: source.ratio || '16:9',
      })),
      subtitles: [],
    };
  }

  if (mediaData.type === BNNVARA_AUDIO_PLAYER_TYPE) {
    mediaData = {
      ...mediaData,
      prid: null,
      duration: media.duration,
      programTitle: media.title,
      genres: media.genres,
      site: getSitenameFromUrl(window.referrer || document.location.href),
      playername: 'bnnvara-web',
      classifications: media.classifications,
      broadcasters: media.broadcasters
        .map(broadcaster => broadcaster.name.toLowerCase())
        .join(','),
      poster: null,
      source: media.sources.map(source => ({
        src: source.url,
        type: source.type || 'audio/mp3',
        name: source.name || 'audio',
        ratio: source.ratio || '12:1',
      })),
      subtitles: null,
    };
  }

  // For testing purposes we add a test subtitle to the video object.
  if (
    mediaData.type === 'video' &&
    !mediaData.subtitles.length &&
    process.env.NODE_ENV === 'development'
  ) {
    mediaData.subtitles.push({
      kind: 'subtitles',
      src:
        'https://rs.poms.omroep.nl/v1/api/subtitles/KN_1700909/nl_NL/CAPTION.vtt',
      language: 'nl',
      srcLang: 'nl',
      label: 'Nederlands',
    });
  }

  return mediaData;
};

/**
 * This component expects an id and a render prop function.
 * Once the data is loaded it will use the render prop to render its children.
 */
export default class MediaDataQuery extends Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    render: PropTypes.func.isRequired,
  };

  renderLegacy() {
    const { id, render } = this.props;

    const {
      data: { media: videoData },
    } = get(window, 'params');

    const video = createMediaData({
      mediaId: id,
      media: {
        ...videoData,
        type: 'video',
        broadcasters: [{ name: videoData.broadcaster }],
      },
    });

    return render(video);
  }

  render() {
    const { id, render } = this.props;

    if (get(window, 'params')) {
      return this.renderLegacy();
    }

    return (
      <Query
        query={getMedia}
        variables={{
          id,
          hasAdConsent: personalAdConsent(),
          atInternetId: atInternetId(),
        }}
      >
        {({ loading, data: mediaData, error }) => {
          if (loading) return null;

          const media = error
            ? createErrorMediaData(error)
            : createMediaData({
                mediaId: id,
                ...mediaData,
              });

          return render(media);
        }}
      </Query>
    );
  }
}
